import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, Loader, EmptyState } from "@/components/common";
import { formatDateTime, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCog, Plus, Loader2, History } from "lucide-react";
import { toast } from "sonner";

const ROLE = {
  owner: { label: "Owner · Full Access", cls: "bg-primary/10 text-primary" },
  manager: { label: "Manager · Sales + Inventory", cls: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" },
  employee: { label: "Employee · Billing", cls: "bg-secondary text-secondary-foreground" },
};

export default function Team() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState(null);
  const [audit, setAudit] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/audit").then((r) => setAudit(r.data));
  };
  useEffect(() => { load(); }, []);
  if (!employees) return <Loader label="Loading team…" />;

  const add = async () => {
    setSaving(true);
    try { await api.post("/employees", form); toast.success("Team member added"); setOpen(false); setForm({ name: "", email: "", password: "", role: "employee" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Team & Audit" subtitle="Control who can access what — and see every change.">
        {user?.role === "owner" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-employee-btn" className="font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add Team Member</Button></DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle className="font-heading">Add Team Member</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input data-testid="emp-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" /></div>
                <div><Label>Email</Label><Input data-testid="emp-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1" /></div>
                <div><Label>Password</Label><Input data-testid="emp-password" type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="mt-1" /></div>
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                    <SelectTrigger data-testid="emp-role" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="manager">Manager</SelectItem><SelectItem value="employee">Employee</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={add} disabled={saving || !form.name || !form.email || !form.password} data-testid="save-employee-btn" className="w-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Team Members" subtitle={`${employees.length} accounts`} testid="team-list">
          <div className="space-y-3">
            {employees.map((e) => (
              <div key={e.user_id} className="flex items-center gap-3 rounded-lg border border-border p-3">
                <Avatar className="h-10 w-10"><AvatarFallback className="bg-primary/15 text-primary text-xs font-bold">{e.name?.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0"><div className="font-medium truncate">{e.name}</div><div className="text-xs text-muted-foreground truncate">{e.email}</div></div>
                <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${ROLE[e.role]?.cls}`}>{ROLE[e.role]?.label.split(" · ")[0]}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Audit Log" subtitle="Every change, tracked" testid="audit-log">
          {audit.length === 0 ? <EmptyState icon={History} title="No activity yet" /> : (
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              {audit.map((a) => (
                <div key={a.id} className="flex gap-3 text-sm border-b border-border/50 pb-3">
                  <History className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div><span className="font-medium">{a.user_name}</span> <span className="text-muted-foreground">{a.action}</span></div>
                    <div className="text-xs text-muted-foreground">{a.entity} · {a.detail}</div>
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(a.timestamp)}</div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
